import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import AudioFileOutlinedIcon from '@mui/icons-material/AudioFileOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useAuth } from '../../context/AuthContext';
import { deleteVideo } from '../../graphqlService';
import { AudioRecording, fetchAudioRecordings } from '../../services/audioRecordingApi';
import { brand } from '../../theme';
import { debugLog } from '../../utils/debugLog';
import { formatBytes } from '../../utils/format';
import Section from '../ui/Section';
import { friendlyErrorMessage } from './friendlyError';
import { formatVideoDuration, joinMeta } from './libraryFormat';

const DELETE_FALLBACK = 'We couldn’t delete this recording. Check your connection and try again.';

/** The message the API gave (a recording still in use says where), or the fallback. */
const apiMessage = (error: unknown): string => {
  const graphQLErrors = (error as { graphQLErrors?: Array<{ message?: string }> })?.graphQLErrors ?? [];
  const message = graphQLErrors.map((item) => item?.message).filter(Boolean)[0];
  return message || friendlyErrorMessage(error, DELETE_FALLBACK);
};

/**
 * The separate audio recordings in the library (a recorder's or mixer's
 * files, added to multicam sets). The video grid leaves them out, but they
 * use storage, so they are listed here where they can be deleted. Nothing is
 * shown until there is one.
 */
const RecordingsList: React.FC = () => {
  const { user } = useAuth();
  const [recordings, setRecordings] = useState<AudioRecording[]>([]);
  const [pending, setPending] = useState<AudioRecording | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await fetchAudioRecordings();
      setRecordings(Array.isArray(list) ? list : []);
    } catch (loadError) {
      debugLog('Recordings could not be listed:', loadError);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const confirmDelete = async () => {
    if (!pending || !user) return;
    setDeleting(true);
    setError(null);
    try {
      const token = await user.getIdToken(true);
      const result = await deleteVideo(pending.id, token);
      if (result?.success) {
        const gone = pending.id;
        setRecordings((current) => current.filter((item) => item.id !== gone));
      } else {
        setError(result?.message || DELETE_FALLBACK);
      }
    } catch (deleteError) {
      setError(apiMessage(deleteError));
    } finally {
      setDeleting(false);
      setPending(null);
    }
  };

  if (recordings.length === 0 && !error) return null;

  return (
    <Box sx={{ mt: 4 }} data-testid="library-recordings">
      <Section
        id="library-recordings"
        title={`Separate audio recordings (${recordings.length})`}
        description="Sound files from a recorder or mixer, added to multicam sets. They count toward your storage."
        disablePadding
      >
        {error && (
          <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box component="ul" aria-label="Separate audio recordings" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {recordings.map((recording, index) => (
            <Box
              component="li"
              key={recording.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.25, borderTop: index === 0 ? 'none' : `1px solid ${brand.border}` }}
            >
              <AudioFileOutlinedIcon sx={{ color: 'text.secondary' }} aria-hidden="true" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={recording.name}>
                  {recording.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {joinMeta([formatVideoDuration(recording.duration), recording.size > 0 ? formatBytes(recording.size) : null])}
                </Typography>
              </Box>
              <Button
                size="small"
                color="inherit"
                startIcon={<DeleteOutlineOutlinedIcon fontSize="small" />}
                onClick={() => setPending(recording)}
                aria-label={`Delete ${recording.name}`}
              >
                Delete
              </Button>
            </Box>
          ))}
        </Box>
      </Section>

      <Dialog open={Boolean(pending)} onClose={() => (deleting ? undefined : setPending(null))} aria-labelledby="delete-recording-title">
        <DialogTitle id="delete-recording-title">Delete this recording?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pending?.name} will be deleted from your library. A multicam set or project that uses it keeps it, so
            it can’t be deleted while one does.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={deleting}>Keep it</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecordingsList;
